import React, { useState, useEffect } from 'react';
import {
  Container, Row, Col, Card, Button, Form, Alert, Spinner,
  Modal, Badge, InputGroup, OverlayTrigger, Tooltip, Tabs, Tab,
  ProgressBar
} from 'react-bootstrap';
import {
  User, Mail, Phone, Lock, Eye, EyeOff, Edit, Save, X,
  Camera, Shield, Building, Calendar, MapPin, CheckCircle,
  AlertCircle, Settings, Bell, Palette, LogOut, RefreshCw,
  Key, UserCheck, Clock, Activity
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useBoutique } from '../../contexts/BoutiqueContext';
import { authService } from '../../services/authService';

const UserProfile = () => {
  const { user, refreshUser, logout } = useAuth();
  const { currentBoutique } = useBoutique();

  // États principaux
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');

  // États pour l'édition du profil
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    username: ''
  });
  const [profileErrors, setProfileErrors] = useState({});

  // États pour le changement de mot de passe
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showReconnectModal, setShowReconnectModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordChanged, setPasswordChanged] = useState(false);

  // États pour les préférences
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    pushNotifications: false,
    theme: 'light',
    language: 'fr'
  });

  // ✅ Charger les données du profil au montage
  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || user.first_name || '',
        last_name: user.last_name || user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        username: user.username || ''
      });
    }
  }, [user]);

  // ✅ Auto-dismiss des alertes
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // ✅ Fonctions de gestion du profil
  const handleProfileInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Nettoyer l'erreur du champ modifié
    if (profileErrors[field]) {
      setProfileErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateProfileData = () => {
    const errors = {};

    if (!profileData.first_name.trim()) {
      errors.first_name = 'Le prénom est obligatoire';
    }

    if (!profileData.last_name.trim()) {
      errors.last_name = 'Le nom est obligatoire';
    }

    if (!profileData.email.trim()) {
      errors.email = 'L\'email est obligatoire';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
      errors.email = 'Format d\'email invalide';
    }

    if (profileData.phone && !/^[+]?[0-9\s\-\(\)]{8,20}$/.test(profileData.phone)) {
      errors.phone = 'Format de téléphone invalide';
    }

    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveProfile = async () => {
    if (!validateProfileData()) {
      setError('Veuillez corriger les erreurs dans le formulaire');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await authService.updateProfile(profileData);

      if (response.success) {
        setSuccess('✅ Profil mis à jour avec succès !');
        setIsEditing(false);
        
        // Actualiser les données utilisateur
        await refreshUser();
      } else {
        throw new Error(response.message);
      }

    } catch (error) {
      console.error('❌ Erreur mise à jour profil:', error);
      setError(error.message || 'Erreur lors de la mise à jour du profil');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    // Restaurer les données originales
    setProfileData({
      first_name: user.first_name || user.first_name || '',
      last_name: user.last_name || user.last_name || '',
      email: user.email || '',
      phone: user.phone || '',
      username: user.username || ''
    });
    setProfileErrors({});
    setIsEditing(false);
  };

  // ✅ Fonctions de gestion du mot de passe
  const handlePasswordInputChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validatePasswordData = () => {
    const errors = {};

    if (!passwordData.currentPassword) {
      errors.currentPassword = 'Mot de passe actuel obligatoire';
    }

    if (!passwordData.newPassword) {
      errors.newPassword = 'Nouveau mot de passe obligatoire';
    } else if (passwordData.newPassword.length < 6) {
      errors.newPassword = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = 'Confirmation du mot de passe obligatoire';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      errors.newPassword = 'Le nouveau mot de passe doit être différent de l\'ancien';
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ✅ NOUVEAU: Gestion du changement de mot de passe avec reconnexion
  const handleChangePassword = async () => {
    if (!validatePasswordData()) {
      return;
    }

    try {
      setLoading(true);
      
      const response = await authService.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.success) {
        setPasswordChanged(true);
        setShowPasswordModal(false);
        setShowReconnectModal(true);
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setShowPasswords({ current: false, new: false, confirm: false });
      } else {
        throw new Error(response.message);
      }

    } catch (error) {
      console.error('❌ Erreur changement mot de passe:', error);
      setPasswordErrors({ submit: error.message || 'Erreur lors du changement de mot de passe' });
    } finally {
      setLoading(false);
    }
  };

  // ✅ NOUVEAU: Forcer la reconnexion
  const handleForceReconnect = () => {
    setShowReconnectModal(false);
    logout();
  };

  // ✅ Utilitaires
  const getRoleBadge = () => {
    const roleConfig = {
      'SUPER_ADMIN': { bg: 'danger', text: 'Super Admin', icon: '👑' },
      'MANAGER': { bg: 'primary', text: 'Gestionnaire', icon: '🏪' },
      'EMPLOYEE': { bg: 'success', text: 'Employé', icon: '👤' }
    };
    
    const config = roleConfig[user?.role] || roleConfig['EMPLOYEE'];
    
    return (
      <Badge bg={config.bg} className="d-flex align-items-center gap-1 fs-6 px-3 py-2">
        <span style={{ fontSize: '1.1em' }}>{config.icon}</span>
        {config.text}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const getInitials = () => {
    const first_name = user?.first_name || user?.first_name || '';
    const last_name = user?.last_name || user?.last_name || '';
    return (first_name.charAt(0) + last_name.charAt(0)).toUpperCase() || 'U';
  };

  // ✅ Calculer la complétude du profil
  const getProfileCompletion = () => {
    const fields = ['first_name', 'last_name', 'email', 'phone'];
    const completedFields = fields.filter(field => 
      user?.[field] || user?.[field.replace('_', '')]
    ).length;
    return Math.round((completedFields / fields.length) * 100);
  };

  if (!user) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3 text-muted fs-5">Chargement du profil...</p>
        </div>
      </Container>
    );
  }

  return (
    <>
      <style jsx>{`
        .profile-container {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          min-height: 100vh;
          padding: 0;
        }
        
        .profile-header {
          background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
          backdrop-filter: blur(10px);
          border-radius: 20px;
          border: 1px solid rgba(255,255,255,0.2);
          margin-bottom: 2rem;
        }
        
        .profile-card {
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 20px;
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
          transition: all 0.3s ease;
        }
        
        .profile-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 40px 0 rgba(31, 38, 135, 0.5);
        }
        
        .avatar-container {
          position: relative;
          display: inline-block;
        }
        
        .avatar {
          width: 120px;
          height: 120px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 48px;
          font-weight: bold;
          color: white;
          border: 4px solid rgba(255,255,255,0.3);
          transition: all 0.3s ease;
        }
        
        .avatar:hover {
          transform: scale(1.05);
        }
        
        .camera-btn {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          border: 3px solid white;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }
        
        .camera-btn:hover {
          transform: scale(1.1);
          background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
        }
        
        .glass-card {
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(10px);
          border-radius: 15px;
          border: 1px solid rgba(255, 255, 255, 0.18);
          padding: 1.5rem;
          margin-bottom: 1rem;
        }
        
        .boutique-info {
          background: linear-gradient(135deg, rgba(103, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
          border-radius: 15px;
          padding: 1.5rem;
          border: 1px solid rgba(103, 126, 234, 0.2);
        }
        
        .stat-item {
          text-align: center;
          padding: 1rem;
          border-radius: 12px;
          background: rgba(255,255,255,0.1);
          backdrop-filter: blur(5px);
          border: 1px solid rgba(255,255,255,0.2);
          transition: all 0.3s ease;
        }
        
        .stat-item:hover {
          background: rgba(255,255,255,0.2);
          transform: translateY(-2px);
        }
        
        .tab-content-card {
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          border: 1px solid rgba(255,255,255,0.2);
          min-height: 500px;
        }
        
        .progress-ring {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: conic-gradient(from 0deg, #667eea 0%, #764ba2 var(--progress, 0%), #e9ecef var(--progress, 0%));
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        
        .progress-ring::before {
          content: '';
          width: 45px;
          height: 45px;
          border-radius: 50%;
          background: white;
          position: absolute;
        }
        
        .progress-text {
          position: relative;
          z-index: 1;
          font-weight: bold;
          font-size: 12px;
        }
        
        .input-group-modern .form-control {
          border: 2px solid rgba(103, 126, 234, 0.1);
          border-radius: 12px;
          padding: 0.75rem 1rem;
          transition: all 0.3s ease;
        }
        
        .input-group-modern .form-control:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(103, 126, 234, 0.25);
          transform: translateY(-2px);
        }
        
        .input-group-modern .input-group-text {
          border: 2px solid rgba(103, 126, 234, 0.1);
          border-radius: 12px 0 0 12px;
          background: rgba(103, 126, 234, 0.05);
        }
        
        .btn-modern {
          border-radius: 12px;
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          transition: all 0.3s ease;
          border: none;
        }
        
        .btn-modern:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15);
        }
        
        .btn-primary.btn-modern {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }
        
        .btn-primary.btn-modern:hover {
          background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
        }
        
        .alert-modern {
          border-radius: 15px;
          border: none;
          backdrop-filter: blur(10px);
          padding: 1rem 1.5rem;
          margin-bottom: 1.5rem;
        }
        
        .alert-success.alert-modern {
          background: rgba(40, 167, 69, 0.1);
          color: #155724;
          border: 1px solid rgba(40, 167, 69, 0.2);
        }
        
        .alert-danger.alert-modern {
          background: rgba(220, 53, 69, 0.1);
          color: #721c24;
          border: 1px solid rgba(220, 53, 69, 0.2);
        }
        
        @media (max-width: 768px) {
          .profile-container {
            padding: 1rem;
          }
          
          .avatar {
            width: 100px;
            height: 100px;
            font-size: 36px;
          }
          
          .camera-btn {
            width: 35px;
            height: 35px;
          }
          
          .tab-content-card {
            margin-top: 1rem;
          }
          
          .glass-card {
            padding: 1rem;
          }
          
          .stat-item {
            padding: 0.75rem;
            margin-bottom: 0.5rem;
          }
        }
        
        @media (max-width: 576px) {
          .btn-modern {
            padding: 0.5rem 1rem;
            font-size: 0.875rem;
          }
          
          .input-group-modern .form-control {
            padding: 0.5rem 0.75rem;
          }
        }
      `}</style>

      <div className="profile-container">
        <Container fluid className="py-4 px-3 px-md-4">
          {/* En-tête moderne */}
          <div className="profile-header text-white p-4 mb-4">
            <Row className="align-items-center">
              <Col>
                <h1 className="h2 mb-1 fw-bold">
                  <User className="me-3" size={32} />
                  Mon Profil
                </h1>
                <p className="mb-0 opacity-75 fs-5">
                  Gérez vos informations personnelles et vos préférences
                </p>
              </Col>
              <Col xs="auto">
                <div 
                  className="progress-ring me-3" 
                  style={{'--progress': `${getProfileCompletion() * 3.6}deg`}}
                >
                  <span className="progress-text">{getProfileCompletion()}%</span>
                </div>
              </Col>
            </Row>
          </div>

          {/* Alertes modernes */}
          {error && (
            <Alert variant="danger" className="alert-modern" dismissible onClose={() => setError(null)}>
              <div className="d-flex align-items-center">
                <AlertCircle size={20} className="me-2 flex-shrink-0" />
                <span>{error}</span>
              </div>
            </Alert>
          )}
          
          {success && (
            <Alert variant="success" className="alert-modern" dismissible onClose={() => setSuccess(null)}>
              <div className="d-flex align-items-center">
                <CheckCircle size={20} className="me-2 flex-shrink-0" />
                <span>{success}</span>
              </div>
            </Alert>
          )}

          <Row className="g-4">
            {/* Sidebar avec informations de base */}
            <Col lg={4}>
              <Card className="profile-card border-0">
                <Card.Body className="text-center p-4">
                  {/* Avatar moderne */}
                  <div className="avatar-container mb-4">
                    <div className="avatar">
                      {getInitials()}
                    </div>
                    <Button className="camera-btn border-0">
                      <Camera size={18} />
                    </Button>
                  </div>

                  {/* Informations principales */}
                  <h3 className="mb-1 fw-bold">
                    {user.first_name || user.first_name} {user.last_name || user.last_name}
                  </h3>
                  <p className="text-muted mb-3 fs-5">@{user.username}</p>
                  
                  <div className="mb-4">
                    {getRoleBadge()}
                  </div>

                  {/* Informations boutique */}
                  {currentBoutique && (
                    <div className="boutique-info mb-4">
                      <div className="d-flex align-items-center justify-content-center mb-2">
                        <Building size={20} className="text-primary me-2" />
                        <strong className="fs-6">{currentBoutique.name}</strong>
                      </div>
                      <small className="text-muted">Boutique assignée</small>
                    </div>
                  )}

                  {/* Statistiques rapides */}
                  <Row className="g-3">
                    <Col xs={6}>
                      <div className="stat-item">
                        <Calendar size={20} className="text-primary mb-2" />
                        <div className="fw-bold fs-6">{formatDate(user.createdAt)}</div>
                        <small className="text-muted">Membre depuis</small>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="stat-item">
                        <Activity size={20} className="text-success mb-2" />
                        <div className="fw-bold fs-6">
                          {user.lastLogin ? formatDate(user.lastLogin) : 'Jamais'}
                        </div>
                        <small className="text-muted">Dernière connexion</small>
                      </div>
                    </Col>
                  </Row>

                  {/* Indicateur de complétude */}
                  <div className="mt-4 p-3 bg-light rounded-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="fw-semibold">Profil complété</span>
                      <span className="text-primary fw-bold">{getProfileCompletion()}%</span>
                    </div>
                    <ProgressBar 
                      now={getProfileCompletion()} 
                      className="rounded-pill"
                      style={{ height: '8px' }}
                    />
                    {getProfileCompletion() < 100 && (
                      <small className="text-muted mt-1 d-block">
                        Complétez votre profil pour une meilleure expérience
                      </small>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Contenu principal avec onglets */}
            <Col lg={8}>
              <Card className="tab-content-card border-0">
                <Card.Header className="bg-transparent border-0 p-4">
                  <Tabs
                    activeKey={activeTab}
                    onSelect={setActiveTab}
                    className="border-0 nav-pills-modern"
                  >
                    <Tab eventKey="profile" title={
                      <span className="d-flex align-items-center">
                        <User size={18} className="me-2" />
                        <span className="d-none d-sm-inline">Profil</span>
                      </span>
                    } />
                    <Tab eventKey="security" title={
                      <span className="d-flex align-items-center">
                        <Lock size={18} className="me-2" />
                        <span className="d-none d-sm-inline">Sécurité</span>
                      </span>
                    } />
                    <Tab eventKey="preferences" title={
                      <span className="d-flex align-items-center">
                        <Settings size={18} className="me-2" />
                        <span className="d-none d-sm-inline">Préférences</span>
                      </span>
                    } />
                  </Tabs>
                </Card.Header>

                <Card.Body className="p-4">
                  {/* Onglet Profil */}
                  {activeTab === 'profile' && (
                    <div>
                      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4">
                        <h4 className="mb-2 mb-sm-0 fw-bold">Informations personnelles</h4>
                        {!isEditing ? (
                          <Button 
                            variant="primary" 
                            className="btn-modern"
                            onClick={() => setIsEditing(true)}
                          >
                            <Edit size={18} className="me-2" />
                            Modifier
                          </Button>
                        ) : (
                          <div className="d-flex gap-2 flex-wrap">
                            <Button 
                              variant="outline-secondary" 
                              className="btn-modern"
                              onClick={handleCancelEdit}
                              disabled={loading}
                            >
                              <X size={18} className="me-2" />
                              Annuler
                            </Button>
                            <Button 
                              variant="primary" 
                              className="btn-modern"
                              onClick={handleSaveProfile}
                              disabled={loading}
                            >
                              {loading ? (
                                <Spinner animation="border" size="sm" className="me-2" />
                              ) : (
                                <Save size={18} className="me-2" />
                              )}
                              Enregistrer
                            </Button>
                          </div>
                        )}
                      </div>

                      <Form>
                        <Row className="g-4">
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold mb-2">Prénom</Form.Label>
                              <InputGroup className="input-group-modern">
                                <InputGroup.Text>
                                  <User size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  value={profileData.first_name}
                                  onChange={(e) => handleProfileInputChange('first_name', e.target.value)}
                                  disabled={!isEditing || loading}
                                  isInvalid={!!profileErrors.first_name}
                                  placeholder="Votre prénom"
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileErrors.first_name}
                                </Form.Control.Feedback>
                              </InputGroup>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold mb-2">Nom</Form.Label>
                              <InputGroup className="input-group-modern">
                                <InputGroup.Text>
                                  <User size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  value={profileData.last_name}
                                  onChange={(e) => handleProfileInputChange('last_name', e.target.value)}
                                  disabled={!isEditing || loading}
                                  isInvalid={!!profileErrors.last_name}
                                  placeholder="Votre nom"
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileErrors.last_name}
                                </Form.Control.Feedback>
                              </InputGroup>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold mb-2">Email</Form.Label>
                              <InputGroup className="input-group-modern">
                                <InputGroup.Text>
                                  <Mail size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                  type="email"
                                  value={profileData.email}
                                  onChange={(e) => handleProfileInputChange('email', e.target.value)}
                                  disabled={!isEditing || loading}
                                  isInvalid={!!profileErrors.email}
                                  placeholder="votre@email.com"
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileErrors.email}
                                </Form.Control.Feedback>
                              </InputGroup>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold mb-2">Téléphone</Form.Label>
                              <InputGroup className="input-group-modern">
                                <InputGroup.Text>
                                  <Phone size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                  type="tel"
                                  value={profileData.phone}
                                  onChange={(e) => handleProfileInputChange('phone', e.target.value)}
                                  disabled={!isEditing || loading}
                                  isInvalid={!!profileErrors.phone}
                                  placeholder="+221 77 123 45 67"
                                />
                                <Form.Control.Feedback type="invalid">
                                  {profileErrors.phone}
                                </Form.Control.Feedback>
                              </InputGroup>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold mb-2">Nom d'utilisateur</Form.Label>
                              <InputGroup className="input-group-modern">
                                <InputGroup.Text>
                                  <UserCheck size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  value={profileData.username}
                                  disabled={true}
                                  className="bg-light"
                                />
                              </InputGroup>
                              <Form.Text className="text-muted">
                                <Lock size={14} className="me-1" />
                                Le nom d'utilisateur ne peut pas être modifié
                              </Form.Text>
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group>
                              <Form.Label className="fw-semibold mb-2">Rôle</Form.Label>
                              <InputGroup className="input-group-modern">
                                <InputGroup.Text>
                                  <Shield size={18} />
                                </InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  value={user.role}
                                  disabled={true}
                                  className="bg-light"
                                />
                              </InputGroup>
                              <Form.Text className="text-muted">
                                <Lock size={14} className="me-1" />
                                Le rôle est défini par l'administrateur
                              </Form.Text>
                            </Form.Group>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  )}

                  {/* Onglet Sécurité */}
                  {activeTab === 'security' && (
                    <div>
                      <h4 className="mb-4 fw-bold">Sécurité du compte</h4>

                      <div className="glass-card mb-4">
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center">
                          <div className="mb-3 mb-sm-0">
                            <h5 className="mb-1 fw-semibold d-flex align-items-center">
                              <Key size={20} className="me-2 text-warning" />
                              Mot de passe
                            </h5>
                            <p className="text-muted mb-0">
                              <Clock size={16} className="me-1" />
                              Dernière modification : {user.passwordChangedAt ? formatDate(user.passwordChangedAt) : 'Inconnue'}
                            </p>
                          </div>
                          <Button 
                            variant="warning" 
                            className="btn-modern"
                            onClick={() => setShowPasswordModal(true)}
                          >
                            <Lock size={18} className="me-2" />
                            Changer
                          </Button>
                        </div>
                      </div>

                      <div className="glass-card">
                        <h5 className="mb-3 fw-semibold">Sessions actives</h5>
                        <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded-3">
                          <div>
                            <div className="fw-semibold">Session actuelle</div>
                            <small className="text-muted">
                              <Activity size={14} className="me-1" />
                              Connexion depuis {user.lastLogin ? formatDate(user.lastLogin) : 'aujourd\'hui'}
                            </small>
                          </div>
                          <Badge bg="success" className="px-3 py-2">
                            <span className="me-1">●</span>
                            Active
                          </Badge>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Onglet Préférences */}
                  {activeTab === 'preferences' && (
                    <div>
                      <h4 className="mb-4 fw-bold">Préférences</h4>

                      <Form>
                        <div className="glass-card mb-4">
                          <h5 className="mb-3 fw-semibold d-flex align-items-center">
                            <Bell size={20} className="me-2 text-primary" />
                            Notifications
                          </h5>
                          <Form.Check
                            type="switch"
                            id="email-notifications"
                            label="Notifications par email"
                            checked={preferences.emailNotifications}
                            onChange={(e) => setPreferences(prev => ({
                              ...prev,
                              emailNotifications: e.target.checked
                            }))}
                            className="mb-3"
                            size="lg"
                          />
                          <Form.Check
                            type="switch"
                            id="push-notifications"
                            label="Notifications push"
                            checked={preferences.pushNotifications}
                            onChange={(e) => setPreferences(prev => ({
                              ...prev,
                              pushNotifications: e.target.checked
                            }))}
                            size="lg"
                          />
                        </div>

                        <div className="glass-card">
                          <h5 className="mb-3 fw-semibold d-flex align-items-center">
                            <Palette size={20} className="me-2 text-success" />
                            Apparence
                          </h5>
                          
                          <Row className="g-3">
                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className="fw-semibold">Thème</Form.Label>
                                <Form.Select
                                  value={preferences.theme}
                                  onChange={(e) => setPreferences(prev => ({
                                    ...prev,
                                    theme: e.target.value
                                  }))}
                                  className="form-control-lg"
                                >
                                  <option value="light">🌞 Clair</option>
                                  <option value="dark">🌙 Sombre</option>
                                  <option value="auto">🔄 Automatique</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>

                            <Col md={6}>
                              <Form.Group>
                                <Form.Label className="fw-semibold">Langue</Form.Label>
                                <Form.Select
                                  value={preferences.language}
                                  onChange={(e) => setPreferences(prev => ({
                                    ...prev,
                                    language: e.target.value
                                  }))}
                                  className="form-control-lg"
                                >
                                  <option value="fr">🇫🇷 Français</option>
                                  <option value="en">🇺🇸 Anglais</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                          </Row>

                          <div className="mt-4">
                            <Button variant="primary" className="btn-modern">
                              <Save size={18} className="me-2" />
                              Enregistrer les préférences
                            </Button>
                          </div>
                        </div>
                      </Form>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Modal changement de mot de passe */}
      <Modal 
        show={showPasswordModal} 
        onHide={() => setShowPasswordModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="d-flex align-items-center">
            <div className="me-3 p-3 bg-warning bg-opacity-10 rounded-circle">
              <Key size={24} className="text-warning" />
            </div>
            <div>
              <h4 className="mb-0">Changer le mot de passe</h4>
              <small className="text-muted">Votre sécurité est importante</small>
            </div>
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="px-4 py-4">
          {passwordErrors.submit && (
            <Alert variant="danger" className="mb-4">
              <AlertCircle size={16} className="me-2" />
              {passwordErrors.submit}
            </Alert>
          )}

          <Form>
            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">Mot de passe actuel</Form.Label>
              <InputGroup className="input-group-modern">
                <InputGroup.Text>
                  <Lock size={18} />
                </InputGroup.Text>
                <Form.Control
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
                  isInvalid={!!passwordErrors.currentPassword}
                  placeholder="Entrez votre mot de passe actuel"
                  size="lg"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPasswords(prev => ({
                    ...prev,
                    current: !prev.current
                  }))}
                  className="border-start-0"
                >
                  {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {passwordErrors.currentPassword}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">Nouveau mot de passe</Form.Label>
              <InputGroup className="input-group-modern">
                <InputGroup.Text>
                  <Key size={18} />
                </InputGroup.Text>
                <Form.Control
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
                  isInvalid={!!passwordErrors.newPassword}
                  placeholder="Entrez le nouveau mot de passe"
                  size="lg"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPasswords(prev => ({
                    ...prev,
                    new: !prev.new
                  }))}
                  className="border-start-0"
                >
                  {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {passwordErrors.newPassword}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label className="fw-semibold">Confirmer le nouveau mot de passe</Form.Label>
              <InputGroup className="input-group-modern">
                <InputGroup.Text>
                  <CheckCircle size={18} />
                </InputGroup.Text>
                <Form.Control
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
                  isInvalid={!!passwordErrors.confirmPassword}
                  placeholder="Confirmez le nouveau mot de passe"
                  size="lg"
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowPasswords(prev => ({
                    ...prev,
                    confirm: !prev.confirm
                  }))}
                  className="border-start-0"
                >
                  {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </Button>
                <Form.Control.Feedback type="invalid">
                  {passwordErrors.confirmPassword}
                </Form.Control.Feedback>
              </InputGroup>
            </Form.Group>

            <Alert variant="info" className="d-flex align-items-start">
              <AlertCircle size={16} className="me-2 mt-1 flex-shrink-0" />
              <div>
                <strong>Important :</strong> Après avoir changé votre mot de passe, 
                vous devrez vous reconnecter pour des raisons de sécurité.
              </div>
            </Alert>
          </Form>
        </Modal.Body>
        
        <Modal.Footer className="border-0 pt-0">
          <Button 
            variant="outline-secondary" 
            onClick={() => setShowPasswordModal(false)}
            disabled={loading}
            className="btn-modern"
          >
            Annuler
          </Button>
          <Button 
            variant="warning" 
            onClick={handleChangePassword}
            disabled={loading}
            className="btn-modern"
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Modification...
              </>
            ) : (
              <>
                <Lock size={18} className="me-2" />
                Changer le mot de passe
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de reconnexion */}
      <Modal 
        show={showReconnectModal} 
        onHide={() => setShowReconnectModal(false)}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header className="border-0 text-center w-100">
          <Modal.Title className="w-100 text-center">
            <div className="mb-3">
              <div className="mx-auto p-4 bg-success bg-opacity-10 rounded-circle d-inline-flex">
                <CheckCircle size={48} className="text-success" />
              </div>
            </div>
            <h4 className="text-success fw-bold">Mot de passe modifié !</h4>
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="text-center px-4 py-4">
          <p className="mb-4 fs-5">
            Votre mot de passe a été modifié avec succès. 
            <br />
            <strong>Vous devez maintenant vous reconnecter.</strong>
          </p>
          
          <Alert variant="warning" className="d-flex align-items-center">
            <AlertCircle size={20} className="me-2 flex-shrink-0" />
            <div className="text-start">
              <strong>Reconnexion requise</strong>
              <br />
              <small>
                Pour votre sécurité, toutes les sessions actives seront fermées.
              </small>
            </div>
          </Alert>
        </Modal.Body>
        
        <Modal.Footer className="border-0 justify-content-center">
          <Button 
            variant="success" 
            onClick={handleForceReconnect}
            className="btn-modern px-4"
            size="lg"
          >
            <LogOut size={20} className="me-2" />
            Se reconnecter maintenant
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default UserProfile;